/**
 * Saglik skoru — backend/app/kpi/health_score.py'nin BIREBIR client portu
 * (docs/faz5-arayuz-spec.md §5: "sağlık skoru client formülü backend ile
 * aynı: eksik KPI paydadan çıkar"). Legacy `frontend/public/js/app.js`
 * `computeHealthScore`'un AKSINE burada `data_status === 'error'` → 0 kurali
 * da uygulanir (backend'te var, eski JS'te YOKTU — bilinen bir sapma).
 */

export interface KpiLike {
  data_status?: string | null;
  count?: number | null;
  critical_count?: number | null;
  warning_count?: number | null;
  [key: string]: unknown;
}

export const HEALTH_WEIGHTS: Record<string, number> = {
  kargolanmamis: 25,
  kargo_yolda: 15,
  destek_iade_talepleri: 30,
  kritik_stok: 15,
  odeme_bekleyen: 15,
};

export const HEALTH_LABELS: Record<string, string> = {
  kargolanmamis: "Henüz kargolanmadı",
  kargo_yolda: "Kargoda yolda",
  destek_iade_talepleri: "Cevap bekleyen destek + iade",
  kritik_stok: "Stoğu azalan ürün",
  odeme_bekleyen: "Ödeme bekleyen sipariş",
};

export interface HealthBreakdownRow {
  kpi_id: string;
  label: string;
  weight: number;
  kpi_score: number | null;
  kpi_count: number;
  kpi_critical: number;
  kpi_warning: number;
  contribution: number;
  lost_points: number;
}

/** Tek KPI icin 0-100 skor — health_score.py `kpi_score`. */
export function kpiScore(kpi: KpiLike | null | undefined): number {
  if (!kpi) return 100;
  if (kpi.data_status === "error") return 0;
  const total = Number(kpi.count ?? 0) || 0;
  if (total === 0) return 100;
  const critical = Number(kpi.critical_count ?? 0) || 0;
  const warning = Number(kpi.warning_count ?? 0) || 0;
  return Math.max(0, 100 - (critical * 15 + warning * 5));
}

/**
 * Agirlikli ortalama; eksik KPI paydadan cikar — health_score.py `health_score`.
 * DUZELTME TURU: backend snapshot'i `kpis["health_score"]` altina duz bir
 * SAYI da gomdugunden (bkz. api/kpi.ts basindaki not) parametre tipi
 * `number`'i de kabul eder — asagidaki `typeof kpi !== 'object'` kontrolu
 * bu degeri zaten guvenle atlar.
 */
export function healthScore(kpis: Record<string, KpiLike | number | null | undefined>): number | null {
  let weightedSum = 0;
  let weightTotal = 0;
  for (const [key, weight] of Object.entries(HEALTH_WEIGHTS)) {
    const kpi = kpis[key];
    if (kpi == null || typeof kpi !== "object") continue;
    weightedSum += kpiScore(kpi) * weight;
    weightTotal += weight;
  }
  if (weightTotal === 0) return null;
  return Math.floor(weightedSum / weightTotal);
}

/** Skor + katki/kayip dokumu — health_score.py `health_score_with_breakdown`. */
export function healthBreakdown(kpis: Record<string, KpiLike | number | null | undefined>): HealthBreakdownRow[] {
  const weightTotal = Object.entries(HEALTH_WEIGHTS).reduce(
    (sum, [key, weight]) => (kpis[key] != null && typeof kpis[key] === "object" ? sum + weight : sum),
    0,
  );
  const rows: HealthBreakdownRow[] = [];
  for (const [key, weight] of Object.entries(HEALTH_WEIGHTS)) {
    const kpi = kpis[key];
    const missing = kpi == null || typeof kpi !== "object";
    const kpiDict: KpiLike = missing ? { count: 0 } : kpi;
    const score = kpiScore(kpiDict);
    const contribution = missing || weightTotal === 0 ? 0 : (score * weight) / weightTotal;
    const maxContribution = missing || weightTotal === 0 ? 0 : weight;
    const lost = maxContribution - contribution;
    rows.push({
      kpi_id: key,
      label: HEALTH_LABELS[key] ?? key,
      weight,
      kpi_score: missing ? null : score,
      kpi_count: Number(kpiDict.count ?? 0) || 0,
      kpi_critical: Number(kpiDict.critical_count ?? 0) || 0,
      kpi_warning: Number(kpiDict.warning_count ?? 0) || 0,
      contribution: Math.round(contribution * 10) / 10,
      lost_points: Math.round(lost * 10) / 10,
    });
  }
  rows.sort((a, b) => b.lost_points - a.lost_points);
  return rows;
}
